<template>
  <div class="document">
    <div class="header">
      <es-header>
        <template #header>
          <div class="header-title">
            Transaction Information
          </div>
        </template>
      </es-header>
    </div>
    <div class="content" style="padding: 1rem 0">
      <es-loader v-if="loading"/>
      <es-exception v-else-if="loadingError"
        exceptionMessage="There was an issue. Please try again or contact your administrator."
      />
      <es-form v-else>
        <template #es-form>
          <div class="row">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">
                  Transaction Type
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    {{ submissionTypeDescription }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">
                  Delimiter
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    {{ fileRecordDelimiter }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">
                  Qualifier
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    {{ fileRecordQualifier }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">
                  Last Transaction ID
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    <router-link
                      :to="{
                        path:`/personId/individualMatch/${lastTransId}/details`,
                      }"
                      class="has-text-weight-bold has-text-primary"
                    >
                      {{ lastTransId }}
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">
                  Batch ID
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    <router-link
                      :to="{
                        path:`/personid/batch/${batchId}/details`,
                      }"
                      class="has-text-weight-bold has-text-primary"
                    >
                      {{ batchId }}
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">
                  Last Update Date
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    {{ person.lastUpdateDate | formatDateMMDDYYYYHHMM }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">
                  Last Update User
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    {{ updateByDisplayName }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">
                  ID Creation Date
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    {{ person.createdDate | formatDateMMDDYYYYHHMM }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">
                  ID Created By
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    {{ createdByDisplayName }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </es-form>
    </div>
  </div>
</template>

<script>
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import FieldLabels from '@/mixins/FieldLabels';

export default {
  mixins: [
    FieldLabels,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
  },
  props: {
    person: {
      type: Object,
      required: true,
    },
    personTransactionInformation: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      lastTransaction: this.personTransactionInformation,
      loading: false,
      loadingError: false,
    };
  },
  computed: {
    createdByDisplayName() {
      return `${this.person.createdByLastName}, ${this.person.createdByFirstName} (${this.person.createdByEmail})`;
    },
    updateByDisplayName() {
      return `${this.person.updateByLastName}, ${this.person.updateByFirstName} (${this.person.updateByEmail})`;
    },
    batchId() {
      return this.lastTransaction.pidBatch.id ? this.lastTransaction.pidBatch.id : undefined;
    },
    lastTransId() {
      if (this.$route.query.recordId) {
        return this.$route.query.recordId;
      }
      return this.person.lastTranSerialNumber;
    },
    fileRecordDelimiter() {
      if (this.lastTransaction.pidBatch.fileRecordDelimiter) {
        if (this.lastTransaction.pidBatch.fileRecordDelimiter.toUpperCase() === '0X09') {
          return 'Tab';
        }
        if (this.lastTransaction.pidBatch.fileRecordDelimiter.toUpperCase() === '0X2C') {
          return 'Comma';
        }
      }
      return null;
    },
    fileRecordQualifier() {
      return this.lastTransaction.pidBatch.fileRecordQualifier
        ? this.lastTransaction.pidBatch.fileRecordQualifier : undefined;
    },
    submissionTypeDescription() {
      return this.lastTransaction.pidBatch.submissionTypeDescription
        ? this.lastTransaction.pidBatch.submissionTypeDescription : undefined;
    },
  },
};
</script>

<style lang="sass" scoped>
  .label
    width: 150px

  .control
    padding-left: 50px
</style>
